import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { fabric } from "fabric";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import { CanvasPaperConfig } from "../designer/PageDesigner/PageDesigner";
import { clampCanvasHeight, getMaxCanvasDimension } from "./canvas-limits";
import { CanvasManager } from "./CanvasManager";
import { useDocumentFonts } from "./fonts/loadDocumentFonts";
import { createPageDividerLines } from "./page-dividers";
import { getNumberOfPages } from "./page-panning";
import { RenderContext } from "./renderer/fabric-renderer";
import { DocumentDesignState } from "./state/types";
import { rearrangeItems } from "./relative-position";
import { throttle } from "lodash";

export interface CanvasProps {
  context: RenderContext;
  dimensions: CanvasPaperConfig;
  state: DocumentDesignState;
  isPreviewing: boolean;
  onStateChange: (state: DocumentDesignState) => void;
  onSelectionChange: (selectedItemId: string | undefined) => void;
  onReady: (manager: CanvasManager) => void;
}

const CanvasRaw: React.FC<CanvasProps> = ({
  context,
  dimensions,
  state,
  isPreviewing,
  onStateChange,
  onSelectionChange,
  onReady,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const managerRef = useRef<CanvasManager>();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const hasReportedClampRef = useRef(false);
  const [scrollHeight, setScrollHeight] = useState("100%");

  // Loads the document's fonts into the browser and clears fabric's metric
  // cache once ready; the returned version re-triggers the render effect below
  // so the canvas re-measures with the real fonts instead of an OS fallback.
  const fontsVersion = useDocumentFonts(state);

  const handleAfterRender = useCallback(() => {
    if (managerRef.current) {
      const newScrollHeight =
        getNumberOfPages(managerRef.current.canvas, dimensions) *
        dimensions.height;
      setScrollHeight(`${newScrollHeight}px`);
    }
  }, [dimensions]);

  useEffect(() => {
    if (!canvasElRef.current) {
      return;
    }

    const manager = new CanvasManager(canvasElRef.current);
    managerRef.current = manager;

    return () => {
      manager.destroy();
      // Leaving the ref set lets later effects and fabric callbacks reach a
      // disposed canvas, whose 2D context fabric has already nulled.
      managerRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    managerRef.current?.setEvents({
      onStateChange,
      onSelectionChange,
      onReady,
      onAfterRender: handleAfterRender,
    });
  }, [onStateChange, onSelectionChange, onReady, handleAfterRender]);

  useEffect(() => {
    const canvas = managerRef.current?.canvas;
    if (!wrapperRef.current || !scrollerRef.current || !canvas) {
      return;
    }

    let canvasHeight = wrapperRef.current.clientHeight;
    let scrollerHeight = scrollerRef.current.clientHeight;
    let pageDividers: fabric.Line[] = [];

    const updateCanvasSize = throttle(
      () => {
        canvas.setWidth(dimensions.width);

        const wantedHeight =
          scrollerHeight < canvasHeight ? scrollerHeight : canvasHeight;

        // fabric scales the backing store by its own pixel ratio, so the browser
        // limit is reached at a smaller CSS height on retina displays. Past that
        // limit the canvas paints nothing at all, without throwing.
        const dpr = fabricPixelRatio();
        const safeHeight = clampCanvasHeight(
          dimensions.width,
          wantedHeight,
          dpr,
          getMaxCanvasDimension(),
        );

        canvas.setHeight(safeHeight);

        if (safeHeight < wantedHeight && !hasReportedClampRef.current) {
          hasReportedClampRef.current = true;
          Sentry.captureMessage("Document designer canvas height clamped", {
            level: "warning",
            extra: {
              wantedHeight,
              safeHeight,
              width: dimensions.width,
              devicePixelRatio: dpr,
              maxCanvasDimension: getMaxCanvasDimension(),
            },
          });
        }
      },
      16,
      { leading: false, trailing: true },
    );

    const canvasResizeObserver = new ResizeObserver((entries) => {
      canvasHeight = entries[0].contentRect.height;
      updateCanvasSize();
    });
    canvasResizeObserver.observe(wrapperRef.current);

    const scrollerResizeObserver = new ResizeObserver((entries) => {
      scrollerHeight = entries[0].contentRect.height;
      updateCanvasSize();

      canvas.remove(...pageDividers);
      pageDividers = createPageDividerLines(canvas, dimensions);
    });
    scrollerResizeObserver.observe(scrollerRef.current);

    return () => {
      // The trailing edge fires up to 16ms late — without this it lands after
      // the canvas has been disposed and writes to an undefined element.
      updateCanvasSize.cancel();
      canvas.remove(...pageDividers);
      canvasResizeObserver.disconnect();
      scrollerResizeObserver.disconnect();
    };
  }, [dimensions]);

  useEffect(() => {
    if (context.allLinkedRecordsFetched) {
      managerRef.current?.render(state, context);
    }
  }, [state, context, fontsVersion]);

  useEffect(() => {
    const canvas = managerRef.current?.canvas;
    if (canvas && canvas._objects.length > 1) {
      onStateChange(rearrangeItems(canvas, state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.record?.id, context.loading]);

  useEffect(() => {
    managerRef.current?.setIsPreviewing(isPreviewing);
  }, [isPreviewing]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>): void => {
      const canvas = managerRef.current?.canvas;
      if (!canvas) return;

      const vpt = canvas.viewportTransform;
      if (vpt) {
        vpt[5] = -event.currentTarget.scrollTop;
        canvas.setViewportTransform(vpt);
        canvas.requestRenderAll();
      }
    },
    [],
  );

  const classes = cx("canvas", {
    "canvas--previewing": isPreviewing,
  });

  return (
    <div ref={wrapperRef} className={classes} onScroll={handleScroll}>
      <div
        ref={scrollerRef}
        className="canvas__scroller"
        style={{ height: scrollHeight }}
      >
        <div className="canvas__wrapper">
          <canvas ref={canvasElRef} className="canvas__body" />
        </div>
      </div>
    </div>
  );
};

/**
 * fabric keeps its own copy of the ratio (`fabric.devicePixelRatio`) and uses that
 * — not `window.devicePixelRatio` — when scaling the backing store. It is missing
 * from `@types/fabric`, hence the cast.
 */
function fabricPixelRatio(): number {
  const fromFabric = (fabric as unknown as { devicePixelRatio?: number })
    .devicePixelRatio;

  return fromFabric ?? window.devicePixelRatio ?? 1;
}

export const Canvas = memo(CanvasRaw);
